import { get, post, put, del } from '@/lib/api/client'
import {
  CreateEventSchema,
  UpdateEventSchema,
  PaginationParamsSchema,
  type Event,
  type CreateEvent,
  type UpdateEvent,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class EventsService {
  /**
   * Create a new event
   */
  async createEvent(data: CreateEvent): Promise<ApiResponse<Event>> {
    const validatedData = CreateEventSchema.parse(data)

    return await post<ApiResponse<Event>>(
      'events/admin/private/create',
      validatedData
    )
  }

  /**
   * Get events with pagination
   */
  async getEvents(params: PaginationParams = { page: 1, size: 10 }): Promise<ApiResponse<PaginatedResponse<Event>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const query = new URLSearchParams(validatedParams as any).toString()

    return await get<ApiResponse<PaginatedResponse<Event>>>(
      `events/admin/private/filter?${query}`
    )
  }

  /**
   * Get all events (unpaginated)
   */
  async getAllEvents(): Promise<ApiResponse<Event[]>> {
    return await get<ApiResponse<Event[]>>(
      'events/admin/private/fetch/all'
    )
  }

  /**
   * Get public events (unpaginated)
   */
  async getPublicEvents(): Promise<ApiResponse<Event[]>> {
    return await get<ApiResponse<Event[]>>(
      'events/public/fetch/all'
    )
  }

  /**
   * Get event by ID
   */
  async getEventById(eventId: string): Promise<ApiResponse<Event>> {
    return await get<ApiResponse<Event>>(
      `events/private/${eventId}`
    )
  }

  /**
   * Search events by title
   */
  async searchEvents(searchBy: string): Promise<ApiResponse<Event[]>> {
    return await post<ApiResponse<Event[]>>(
      'events/admin/private/search',
      { searchBy }
    )
  }

  /**
   * Update event
   */
  async updateEvent(eventId: string, data: Partial<UpdateEvent>): Promise<ApiResponse<Event>> {
    const validatedData = UpdateEventSchema.partial().parse(data)

    return await put<ApiResponse<Event>>(
      `events/admin/private/edit/${eventId}`,
      validatedData
    )
  }

  /**
   * Update event status
   */
  async updateEventStatus(
    eventId: string,
    status: 'DRAFT' | 'PUBLISH'
  ): Promise<ApiResponse<Event>> {
    return await put<ApiResponse<Event>>(
      `events/admin/private/${eventId}/status`,
      { status }
    )
  }

  /**
   * Delete event
   */
  async deleteEvent(eventId: string): Promise<ApiResponse<void>> {
    return await del<ApiResponse<void>>(
      `events/admin/private/${eventId}`
    )
  }

  /**
   * Get upcoming events
   */
  async getUpcomingEvents(): Promise<ApiResponse<Event[]>> {
    return await get<ApiResponse<Event[]>>(
      'events/admin/private/upcoming'
    )
  }

  /**
   * Get ongoing events
   */
  async getOngoingEvents(): Promise<ApiResponse<Event[]>> {
    return await get<ApiResponse<Event[]>>(
      'events/admin/private/ongoing'
    )
  }

  /**
   * Get completed events
   */
  async getCompletedEvents(): Promise<ApiResponse<Event[]>> {
    return await get<ApiResponse<Event[]>>(
      'events/admin/private/completed'
    )
  }

  /**
   * Get total events count
   */
  async getTotalEvents(): Promise<ApiResponse<{ count: number }>> {
    return await get<ApiResponse<{ count: number }>>(
      'events/admin/private/total'
    )
  }

  /**
   * Get events by category
   */
  async getEventsByCategory(category: string): Promise<ApiResponse<Event[]>> {
    return await get<ApiResponse<Event[]>>(
      `events/admin/private/category/${category}`
    )
  }

  /**
   * Get event categories
   */
  async getEventCategories(): Promise<ApiResponse<string[]>> {
    return await get<ApiResponse<string[]>>(
      'events/admin/private/categories'
    )
  }

  /**
   * Upload event image (simplified - would need multipart form implementation)
   */
  async uploadEventImage(_file: File, _eventId?: string): Promise<ApiResponse<{ url: string }>> {
    // TODO: Implement file upload with fetch FormData
    // For now, return a placeholder response
    throw new Error('File upload not implemented with simple fetch client')
  }

  /**
   * Register for event (if registration endpoint exists)
   */
  async registerForEvent(eventId: string, userData: any): Promise<ApiResponse<void>> {
    return await post<ApiResponse<void>>(
      `events/admin/private/register/${eventId}`,
      userData
    )
  }

  /**
   * Get event registrations
   */
  async getEventRegistrations(eventId: string): Promise<ApiResponse<any[]>> {
    return await get<ApiResponse<any[]>>(
      `events/admin/private/registrations/${eventId}`
    )
  }
}

// Export singleton instance
export const eventsService = new EventsService()

// Export default
export default eventsService